/**
 * @ngdoc service
 * @name momentService
 * @module flowingly.services
 *
 * @description A helper service for api/procedures/ actions.
 *
 */

import IPublishProcedure from '@Shared.Angular/@types/core/contracts/commands/procedures/publishProcedure';
import { PublicType } from '@Shared.Angular/@types/core/contracts/constants/publicType';
import IProcedureActionDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionDetail';
import IProcedureDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureDetail';
import IProcedureOverview from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureOverview';
import IProcedureSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureSummary';
import IProcedureTagSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureTagSummary';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { Services } from '@Shared.Angular/@types/services';
import angular, { IHttpService } from 'angular';

export class ProcedureApiService {
  private apiBaseUrl: string;
  constructor(
    private $http: IHttpService,
    private APP_CONFIG: Services.APP_CONFIG
  ) {
    this.apiBaseUrl = this.APP_CONFIG.apiBaseUrl + 'procedures/';
  }

  createProcedure(procedureName: string) {
    return this.$http
      .post<Guid>(this.apiBaseUrl, {
        name: procedureName
      })
      .then((response) => {
        return response.data;
      });
  }

  copyProcedure(existingProcedureId: Guid, newProcedureName: string) {
    return this.$http
      .post<Guid>(this.apiBaseUrl + existingProcedureId, {
        newProcedureName
      })
      .then((response) => {
        return response.data;
      });
  }

  publishProcedure(command: IPublishProcedure) {
    return this.$http
      .put<IProcedureDetail>(
        this.apiBaseUrl + command.id + '/published',
        command
      )
      .then((response) => {
        return response.data;
      });
  }

  getProcedures() {
    return this.$http
      .get<IProcedureOverview[]>(this.apiBaseUrl)
      .then((response) => {
        return response.data;
      });
  }

  getProceduresPublishedToCurrentUser() {
    return this.$http
      .get<IProcedureSummary[]>(this.apiBaseUrl + 'published')
      .then((response) => {
        return response.data;
      });
  }

  getProcedure(id: Guid, recordView = false, isEditing = false) {
    return this.$http
      .get<IProcedureDetail>(
        this.apiBaseUrl +
          id +
          `?recordView=${recordView}&isEditing=${isEditing}`
      )
      .then((response) => {
        response.data.actions.sort((a, b) => a.order - b.order);
        return response.data;
      });
  }

  getPublicProcedure(id: Guid, businessId?: Guid | null) {
    return this.$http
      .get<IProcedureDetail>(
        this.APP_CONFIG.apiBaseUrl +
          'procedure/' +
          id +
          (businessId ? `?businessId=${businessId}` : '')
      )
      .then((response) => {
        return response.data;
      });
  }

  updateProcedure(procedure: IProcedureDetail) {
    return this.$http.put(this.apiBaseUrl + procedure.id, procedure);
  }

  updateProcedureAction(action: IProcedureActionDetail) {
    return this.$http.put(
      `${this.apiBaseUrl}${action.procedureId}/actions/${action.id}`,
      action
    );
  }

  addProcedureAction(action: IProcedureActionDetail) {
    return this.$http
      .post<Guid>(`${this.apiBaseUrl}${action.procedureId}/actions`, action)
      .then((response) => {
        return response.data;
      });
  }

  updateProcedurePublicType(id: Guid, publicType: PublicType | null) {
    return this.$http.put(
      `${this.apiBaseUrl}${id}/publicType/${
        publicType === null ? '' : publicType
      }`,
      null
    );
  }

  deleteProcedure(procedureId: Guid) {
    return this.$http.delete(this.apiBaseUrl + procedureId);
  }

  deleteProcedureAction(procedureId: Guid, actionId: Guid) {
    return this.$http.delete(
      `${this.apiBaseUrl}${procedureId}/actions/${actionId}`
    ) as Promise<unknown>;
  }

  getProcedurePublishedTo(procedureId: Guid) {
    return this.$http
      .get<IFlowinglySearchResult[]>(
        this.apiBaseUrl + procedureId + '/publishedEntities'
      )
      .then((response) => {
        return response.data;
      });
  }

  getProcedureTags(procedureId: Guid) {
    return this.$http
      .get<ITagGroupDetail[]>(this.apiBaseUrl + procedureId + '/tags')
      .then((response) => {
        return response.data;
      });
  }

  getUsedTagsInProcedures() {
    return this.$http
      .get<IProcedureTagSummary[]>(this.apiBaseUrl + 'tags')
      .then((response) => {
        return response.data;
      });
  }
}

angular
  .module('flowingly.services')
  .factory('procedureApiService', [
    '$http',
    'APP_CONFIG',
    ($http: IHttpService, appConfig: Services.APP_CONFIG) =>
      new ProcedureApiService($http, appConfig)
  ]);

export type ProcedureApiServiceType = InstanceType<typeof ProcedureApiService>;
